import { toast } from '@repo/design-system/components/ui/toaster';
import {
    useCreateMapLocationFeatureMutation,
    useDeleteMapLocationFeatureMutation,
    useUpdateMapLocationFeatureMutation,
} from '@repo/store/maps/api/mapsApi';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import {
    addMapLocationFeature,
    selectEditingMapLocationId,
    selectExternalMapId,
    selectIsAddingMapLocation,
    selectIsEditingMapLocation,
    selectIsMovingLocation,
    selectLastSelectedCategoryId,
    selectLastSelectedSubcategoryId,
    selectMapSubcategories,
    selectMovingLocationId,
    setIsAddingMapLocation,
    setIsMovingLocation,
} from '@repo/store/maps/slices/mapData';
import { CUSTOM_PINS_CATEGORY_ID } from '@repo/store/maps/utils/mapData';
import type { MapDataLocationFeature } from '@repo/types/maps/map-types';
import type maplibregl from 'maplibre-gl';
import { useCallback, useEffect, useRef } from 'react';
import { useMapContext } from '../map-context';
import { useMapCursor } from './useMapCursor';
import { useMapPinOperations } from './useMapPinOperations';

type OverrideValues = { lastSelectedCategoryId: string | null; lastSelectedSubcategoryId: string | null };

export function useCreatorToolOperations() {
    const dispatch = useAppDispatch();
    const { map } = useMapContext();
    const externalId = useAppSelector(selectExternalMapId);
    const isAddingMapLocation = useAppSelector(selectIsAddingMapLocation);
    const isEditingMapLocation = useAppSelector(selectIsEditingMapLocation);
    const editingMapLocationId = useAppSelector(selectEditingMapLocationId);
    const isMovingLocation = useAppSelector(selectIsMovingLocation);
    const movingLocationId = useAppSelector(selectMovingLocationId);
    const subcategories = useAppSelector(selectMapSubcategories);
    const lastSelectedCategoryId = useAppSelector(selectLastSelectedCategoryId);
    const lastSelectedSubcategoryId = useAppSelector(selectLastSelectedSubcategoryId);

    const { setCursor } = useMapCursor();
    const { selectMarkerInMap } = useMapPinOperations();

    const [createMapLocationFeature, { isLoading: isCreating }] = useCreateMapLocationFeatureMutation();
    const [updateMapLocationFeature, { isLoading: isUpdating }] = useUpdateMapLocationFeatureMutation();
    const [deleteMapLocationFeature, { isLoading: isDeleting }] = useDeleteMapLocationFeatureMutation();

    // Store the event handler references
    const eventHandlersRef = useRef<{
        mapClick: ((e: maplibregl.MapMouseEvent) => void) | null;
        escapeHandler: ((e: KeyboardEvent) => void) | null;
        clickOutside: ((e: MouseEvent) => void) | null;
    }>({
        mapClick: null,
        escapeHandler: null,
        clickOutside: null,
    });
    const isCreatingPinRef = useRef(false);

    // Shared cleanup function to remove event listeners
    const cleanupEventListeners = useCallback(() => {
        const { mapClick, escapeHandler, clickOutside } = eventHandlersRef.current;

        if (map && mapClick) {
            map.off('click', mapClick);
            eventHandlersRef.current.mapClick = null;
        }
        if (escapeHandler) {
            document.removeEventListener('keydown', escapeHandler);
            eventHandlersRef.current.escapeHandler = null;
        }
        if (clickOutside) {
            document.removeEventListener('click', clickOutside);
            eventHandlersRef.current.clickOutside = null;
        }

        setCursor('grab');
    }, [map, setCursor]);

    // Shared function to create escape and click-outside handlers
    const createSharedHandlers = useCallback(
        (onCancel: () => void) => {
            const handleEscape = (e: KeyboardEvent) => {
                if (e.key === 'Escape') {
                    e.preventDefault();
                    onCancel();
                }
            };

            const handleClickOutside = (e: MouseEvent) => {
                const target = e.target as Element;
                const mapContainer = map?.getContainer();
                if (mapContainer && !mapContainer.contains(target)) {
                    onCancel();
                }
            };

            return { handleEscape, handleClickOutside };
        },
        [map],
    );

    // Shared function to set up all event listeners
    const setupEventListeners = useCallback(
        (mapClickHandler: (e: maplibregl.MapMouseEvent) => void, onCancel: () => void, targetMap?: maplibregl.Map) => {
            const mapTarget = targetMap || map;
            if (!mapTarget) return;

            const { handleEscape, handleClickOutside } = createSharedHandlers(onCancel);

            // Store handlers in ref for cleanup
            eventHandlersRef.current = {
                mapClick: mapClickHandler,
                escapeHandler: handleEscape,
                clickOutside: handleClickOutside,
            };

            // Add event listeners
            mapTarget.on('click', mapClickHandler);
            document.addEventListener('keydown', handleEscape);
            document.addEventListener('click', handleClickOutside);

            // Set cursor
            setCursor('crosshair');
        },
        [map, createSharedHandlers, setCursor],
    );

    useEffect(() => {
        if (!isAddingMapLocation && !isMovingLocation) {
            cleanupEventListeners();
        }
    }, [isAddingMapLocation, isMovingLocation, cleanupEventListeners]);

    useEffect(() => {
        return cleanupEventListeners;
    }, [cleanupEventListeners]);

    const createMapLocationAtCoordinates = useCallback(
        async (lng: number, lat: number, cloneLocation?: MapDataLocationFeature, overrideValues?: OverrideValues) => {
            if (!externalId) return null;

            const categoryId = overrideValues?.lastSelectedCategoryId ?? lastSelectedCategoryId;
            const subcategoryId = overrideValues?.lastSelectedSubcategoryId ?? lastSelectedSubcategoryId;

            let selectedSubcategory = subcategories?.find(sub => sub.externalId === subcategoryId);

            if (!selectedSubcategory && categoryId) {
                selectedSubcategory = subcategories?.find(sub => sub.categoryExternalId === categoryId);
            }

            if (!selectedSubcategory) {
                selectedSubcategory = subcategories?.find(sub => sub.categoryExternalId !== CUSTOM_PINS_CATEGORY_ID);
            }

            if (!selectedSubcategory) {
                toast({
                    title: 'No subcategories available',
                    variant: 'error',
                });
                return null;
            }

            try {
                const newLocation = await createMapLocationFeature({
                    gameMapExternalId: externalId,
                    location: {
                        type: 'Feature',
                        geometry: { type: 'Point', coordinates: [lng, lat] },
                        properties: {
                            title: cloneLocation?.properties.title ?? '',
                            description: cloneLocation?.properties.description ?? '',
                            subcategoryExternalId:
                                cloneLocation?.properties.subcategoryExternalId ?? selectedSubcategory.externalId,
                            imageId: null,
                            videoId: null,
                            imagePath: null,
                            videoPath: null,
                            gameCoords: cloneLocation?.properties.gameCoords ?? undefined,
                        },
                    },
                }).unwrap();

                return newLocation;
            } catch (error) {
                toast({
                    title: 'Failed to create location',
                    description: JSON.stringify(error),
                    variant: 'error',
                });
                return null;
            }
        },
        [externalId, subcategories, lastSelectedCategoryId, lastSelectedSubcategoryId, createMapLocationFeature],
    );

    const startAddingMapLocation = useCallback(
        (cloneLocation?: MapDataLocationFeature) => {
            if (!map || !externalId) return;

            dispatch(setIsAddingMapLocation(true));

            const handleCancel = () => {
                dispatch(setIsAddingMapLocation(false));
                cleanupEventListeners();
            };

            const handleMapClick = async (e: maplibregl.MapMouseEvent) => {
                if (isCreatingPinRef.current) return;

                const { lng, lat } = e.lngLat;
                isCreatingPinRef.current = true;

                try {
                    const newLocation = await createMapLocationAtCoordinates(lng, lat, cloneLocation);
                    if (newLocation) {
                        dispatch(addMapLocationFeature(newLocation));
                        selectMarkerInMap(newLocation.properties.externalId, {
                            feature: newLocation,
                            isNewlyCreated: true,
                        });
                    }
                } catch {
                    // Error handling is already done in createMapLocationAtCoordinates
                } finally {
                    isCreatingPinRef.current = false;
                    handleCancel();
                }
            };

            setupEventListeners(handleMapClick, handleCancel);
        },
        [
            map,
            externalId,
            dispatch,
            cleanupEventListeners,
            createMapLocationAtCoordinates,
            selectMarkerInMap,
            setupEventListeners,
        ],
    );

    const cancelAddingMapLocation = useCallback(() => {
        dispatch(setIsAddingMapLocation(false));
        cleanupEventListeners();
    }, [dispatch, cleanupEventListeners]);

    const handleSaveMapLocation = useCallback(
        async (location: MapDataLocationFeature) => {
            if (!externalId) return;

            try {
                await updateMapLocationFeature({
                    location,
                    gameMapExternalId: externalId,
                });
            } catch (error) {
                toast({ title: 'Failed to save location.', description: JSON.stringify(error), variant: 'error' });
            }
        },
        [updateMapLocationFeature, externalId],
    );

    const handleDeleteMapLocation = useCallback(
        async (location: MapDataLocationFeature) => {
            if (!externalId) return;

            try {
                await deleteMapLocationFeature({
                    location,
                    gameMapExternalId: externalId,
                });
            } catch (error) {
                toast({ title: 'Failed to delete location.', description: JSON.stringify(error), variant: 'error' });
            }
        },
        [deleteMapLocationFeature, externalId],
    );

    const startCloningLocation = useCallback(
        (location: MapDataLocationFeature) => {
            startAddingMapLocation(location);
        },
        [startAddingMapLocation],
    );

    const startMovingLocation = useCallback(
        (location: MapDataLocationFeature, targetMap?: maplibregl.Map) => {
            const mapTarget = targetMap || map;
            if (!mapTarget || !externalId) return;

            dispatch(setIsMovingLocation({ isMoving: true, locationId: location.properties.externalId }));

            const handleCancel = () => {
                dispatch(setIsMovingLocation({ isMoving: false, locationId: null }));
                cleanupEventListeners();
            };

            const handleMapClick = async (e: maplibregl.MapMouseEvent) => {
                const { lng, lat } = e.lngLat;

                try {
                    await updateMapLocationFeature({
                        location: {
                            ...location,
                            geometry: { type: 'Point' as const, coordinates: [lng, lat] as [number, number] },
                        },
                        gameMapExternalId: externalId,
                    });
                } catch (error) {
                    toast({ title: 'Failed to move location.', description: JSON.stringify(error), variant: 'error' });
                } finally {
                    handleCancel();
                }
            };

            setupEventListeners(handleMapClick, handleCancel, mapTarget);
        },
        [map, externalId, dispatch, cleanupEventListeners, updateMapLocationFeature, setupEventListeners],
    );

    const cancelMovingLocation = useCallback(() => {
        dispatch(setIsMovingLocation({ isMoving: false, locationId: null }));
        cleanupEventListeners();
    }, [dispatch, cleanupEventListeners]);

    return {
        isAddingMapLocation,
        isEditingMapLocation,
        editingMapLocationId,
        isMovingLocation,
        movingLocationId,
        startAddingMapLocation,
        startCloningLocation,
        startMovingLocation,
        cancelAddingMapLocation,
        cancelMovingLocation,
        createMapLocationAtCoordinates,
        isCreating,
        isUpdating,
        isDeleting,
        handleSaveMapLocation,
        handleDeleteMapLocation,
    };
}
